import type { LucideIcon } from 'lucide-react';
import { Clock, Plug, Settings, ShieldAlert, Tags, Upload } from 'lucide-react';

/**
 * Registry for the Admin area's vertical tabs (iPlan4-style quick-navigate
 * admin section). Single source of truth consumed by:
 * - `AdminLayout` — renders the grouped tab rail and search filter
 * - `router.tsx` — child routes under `/admin` and redirects from the old
 *   top-level paths
 * - `RootLayout`'s `buildBreadcrumbs` — "Admin › <tab>" trails (the single
 *   `/admin` nav entry prefix-matches every `/admin/*` path, so the trail
 *   must come from this registry rather than the nav structure)
 */

export interface AdminTab {
  key: string;
  label: string;
  /** Full path, e.g. `/admin/site-time-codes`. */
  href: string;
  icon: LucideIcon;
  /** The pre-admin-area top-level path this tab used to live at. */
  legacyPath: string;
}

export interface AdminTabGroup {
  key: string;
  label: string;
  tabs: AdminTab[];
}

export const ADMIN_TAB_GROUPS: AdminTabGroup[] = [
  {
    key: 'configuration',
    label: 'Configuration',
    tabs: [
      {
        key: 'site-time-codes',
        label: 'Site Time Codes',
        href: '/admin/site-time-codes',
        icon: Clock,
        legacyPath: '/site-time-codes',
      },
      {
        key: 'field-admin',
        label: 'Field Admin',
        href: '/admin/field-admin',
        icon: Settings,
        legacyPath: '/field-admin',
      },
      {
        key: 'sundry-categories',
        label: 'Sundry Categories',
        href: '/admin/sundry-categories',
        icon: Tags,
        legacyPath: '/sundry-categories',
      },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    tabs: [
      {
        key: 'triage',
        label: 'Triage',
        href: '/admin/triage',
        icon: ShieldAlert,
        legacyPath: '/triage',
      },
    ],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    tabs: [
      {
        key: 'integrations',
        label: 'Integrations',
        href: '/admin/integrations',
        icon: Plug,
        legacyPath: '/integrations',
      },
      {
        key: 'import-history',
        label: 'Import History',
        href: '/admin/import-history',
        icon: Upload,
        legacyPath: '/import-history',
      },
    ],
  },
];

export const ADMIN_TABS: AdminTab[] = ADMIN_TAB_GROUPS.flatMap((group) => group.tabs);

/** The tab `/admin` itself redirects to. */
export const DEFAULT_ADMIN_TAB = ADMIN_TABS[0]!;

/** The admin tab (and its group) matching a pathname, if any. */
export function findAdminTab(
  pathname: string,
): { group: AdminTabGroup; tab: AdminTab } | undefined {
  for (const group of ADMIN_TAB_GROUPS) {
    for (const tab of group.tabs) {
      if (pathname === tab.href || pathname.startsWith(`${tab.href}/`)) {
        return { group, tab };
      }
    }
  }
  return undefined;
}
